'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useParams, useSearchParams } from 'react-router-dom';
import { Paperclip, Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '@/lib/axios';
import { reportCreatePostFailure } from '@/lib/capture-create-post-error';
import { uploadFilesToVertex } from '@/lib/vertex-upload';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { useFrequentMembers } from '@/hooks/use-frequent-members';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormComboboxField } from '@/components/forms/form-combobox-field';
import { FormMultiSelectField } from '@/components/forms/form-multi-select-field';
import { FormRadioGroupField } from '@/components/forms/form-radio-group-field';
import { FormPhoneField } from '@/components/forms/form-phone-field';
import { SectionLabel } from '@/components/forms/section-label';
import { FileAttachmentList } from '@/components/forms/file-attachment-list';
import { FileUploadButton } from '@/components/forms/file-upload-button';

// Mock Data for "Who can fulfill" - In real app, fetch from API
const FULFILLER_OPTIONS: { label: string; value: string }[] = [
  { label: 'Web Developers', value: 'web_dev' },
  { label: 'Designers', value: 'designer' },
  { label: 'Marketing Agencies', value: 'marketing' },
  { label: 'Consultants', value: 'consultant' },
  { label: 'Legal', value: 'legal' },
  { label: 'Interior Designers', value: 'interior' },
];

const BENEFICIARY_OPTIONS = [
  { value: 'self', label: 'Myself' },
  { value: 'other', label: 'Someone else' },
];

const FULFILL_TYPE_OPTIONS = [
  { value: 'open', label: 'Anyone (Open)' },
  { value: 'directed', label: 'Specific Person' },
];

const URGENCY_OPTIONS = ['Now', '15 Days', '3 Months', '6 Months', 'Likely', 'Relational'].map(
  (value) => ({ value, label: value })
);

const leadFormSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  beneficiary: z.enum(['self', 'other']),
  beneficiaryName: z.string().optional(),
  beneficiaryContact: z.string().optional(),
  beneficiaryEmail: z.string().optional(),
  beneficiaryRelation: z.string().optional(),

  requirement: z
    .string()
    .min(10, 'Please describe the requirement in detail')
    .max(1000, 'Requirement cannot exceed 1000 characters'),
  fulfillType: z.enum(['open', 'directed']),
  fulfillers: z.array(z.string()).optional(),
  toUserId: z.string().optional(),
  urgency: z.string().min(1, 'Please select when this is required'),
});

interface LeadFormProps {
  onCancel: () => void;
  onSubmit: (postId: string) => void;
}

export function LeadForm({ onCancel, onSubmit: parentSubmit }: LeadFormProps) {
  const [images, setImages] = useState<File[]>([]);
  const [docs, setDocs] = useState<File[]>([]);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');

  const form = useForm<z.infer<typeof leadFormSchema>>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      title: '',
      beneficiary: 'self',
      beneficiaryName: '',
      beneficiaryContact: '',
      beneficiaryEmail: '',
      beneficiaryRelation: '',
      requirement: '',
      fulfillType: 'open',
      fulfillers: [],
      toUserId: '',
      urgency: 'Now',
    },
  });

  const beneficiaryType = form.watch('beneficiary');
  const fulfillType = form.watch('fulfillType');
  const beneficiaryContact = form.watch('beneficiaryContact');

  const { communityId } = useParams<{ communityId: string }>();
  const [searchParams] = useSearchParams();
  const prefillMemberId = searchParams.get('memberId');

  useEffect(() => {
    if (prefillMemberId) {
      form.setValue('fulfillType', 'directed');
      form.setValue('toUserId', prefillMemberId);
      form.setValue('fulfillers', [prefillMemberId]);
    }
  }, [prefillMemberId, form]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  const [categoriesMap, setCategoriesMap] = useState<
    Map<string, { label: string; value: string; searchValue: string }>
  >(new Map());

  const { members, currentUserId } = useCommunityMembers(communityId!);
  const { members: frequentMembers } = useFrequentMembers(currentUserId, communityId);

  const fetchCategories = useCallback(async (q: string = '') => {
    try {
      const res = await axiosInstance.get(`/api/categories?q=${encodeURIComponent(q)}`);
      if (res.data.categories) {
        setCategoriesMap((prev) => {
          const newMap = new Map(prev);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          res.data.categories.forEach((c: any) => {
            newMap.set(c.label, {
              label: c.subCategory,
              value: c.label, // "Category > SubCategory"
              searchValue: `${c.category} ${c.subCategory}`,
            });
          });
          return newMap;
        });
      }
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCategorySearch = useCallback(
    (val: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (val.trim().length > 0) {
          fetchCategories(val.trim());
        }
      }, 500);
    },
    [fetchCategories]
  );

  const handlePredictCategory = async () => {
    const title = form.getValues('title');
    const requirement = form.getValues('requirement');

    if (!title || !requirement || title.length < 5 || requirement.length < 10) {
      toast.error('Please enter a descriptive title and requirement first.');
      return;
    }

    setIsPredicting(true);
    try {
      const res = await axiosInstance.post('/api/categories/predict', {
        title,
        description: requirement,
      });

      if (
        res.data.predictedCategories &&
        Array.isArray(res.data.predictedCategories) &&
        res.data.predictedCategories.length > 0
      ) {
        const predictedArray = res.data.predictedCategories;

        setCategoriesMap((prev) => {
          let hasChanges = false;
          const newMap = new Map(prev);
          predictedArray.forEach((predicted: string) => {
            if (!newMap.has(predicted)) {
              hasChanges = true;
              const parts = predicted.split(' > ');
              newMap.set(predicted, {
                label: parts[1] || predicted,
                value: predicted,
                searchValue: predicted,
              });
            }
          });
          return hasChanges ? newMap : prev;
        });

        const currentFulfillers = form.getValues('fulfillers') || [];
        const newFulfillers = [...currentFulfillers];
        let addedCount = 0;

        predictedArray.forEach((predicted: string) => {
          if (!newFulfillers.includes(predicted)) {
            newFulfillers.push(predicted);
            addedCount++;
          }
        });

        if (addedCount > 0) {
          form.setValue('fulfillers', newFulfillers, { shouldValidate: true });
          toast.success(`AI Predicted ${addedCount} Categor${addedCount > 1 ? 'ies' : 'y'}`);
        } else {
          toast.info('AI predictions are already selected.');
        }
      } else {
        toast.error('AI could not confidently predict any categories.');
      }
    } catch (error) {
      console.error('Category prediction failed:', error);
      toast.error('Failed to predict category with AI.');
    } finally {
      setIsPredicting(false);
    }
  };

  const categoryOptions = Array.from(categoriesMap.values());

  useEffect(() => {
    if (
      beneficiaryType === 'other' &&
      beneficiaryContact &&
      beneficiaryContact.replace(/\\D/g, '').length >= 10
    ) {
      const fetchUserData = async () => {
        try {
          const res = await axiosInstance.get(
            `/api/user/public/by-phone?phone=${encodeURIComponent(beneficiaryContact)}`
          );
          if (res.data.found) {
            const { name, email } = res.data.user;
            if (name && !form.getValues('beneficiaryName')) {
              form.setValue('beneficiaryName', name, { shouldValidate: true });
            }
            if (email && !form.getValues('beneficiaryEmail')) {
              form.setValue('beneficiaryEmail', email, { shouldValidate: true });
            }
          }
        } catch (error) {
          console.error('Failed to fetch user by phone', error);
        }
      };

      const timer = setTimeout(fetchUserData, 500);
      return () => clearTimeout(timer);
    }
  }, [beneficiaryContact, beneficiaryType, form]);

  // Custom check logic for dependent fields (zod superRefine would be tidier but this
  // preserves the existing UX of validating on submit).
  const onSubmit = async (values: z.infer<typeof leadFormSchema>) => {
    if (values.beneficiary === 'other') {
      let hasError = false;
      if (!values.beneficiaryName) {
        form.setError('beneficiaryName', { message: 'Name is required' });
        hasError = true;
      }
      if (!values.beneficiaryContact) {
        form.setError('beneficiaryContact', { message: 'Contact is required' });
        hasError = true;
      }
      if (!values.beneficiaryRelation) {
        form.setError('beneficiaryRelation', { message: 'Relation is required' });
        hasError = true;
      }

      if (hasError) return;
    }

    if (values.fulfillType === 'directed' && !values.toUserId) {
      form.setError('toUserId', { message: 'Please select a person to fulfill this lead' });
      return;
    }

    if (values.fulfillType === 'open' && (!values.fulfillers || values.fulfillers.length === 0)) {
      form.setError('fulfillers', { message: 'Select at least one category' });
      return;
    }

    setIsSubmitting(true);
    const hasFiles = images.length > 0 || docs.length > 0;
    setSubmitPhase(hasFiles ? 'uploading' : 'saving');

    try {
      const imageUrls = images.length > 0 ? await uploadFilesToVertex(images, 'leads') : [];
      const documentUrls = docs.length > 0 ? await uploadFilesToVertex(docs, 'leads') : [];
      const attachments = [...imageUrls, ...documentUrls];

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('beneficiary', values.beneficiary);
      if (values.beneficiaryName) formData.append('beneficiaryName', values.beneficiaryName);
      if (values.beneficiaryContact)
        formData.append('beneficiaryContact', values.beneficiaryContact);
      if (values.beneficiaryEmail) formData.append('beneficiaryEmail', values.beneficiaryEmail);
      if (values.beneficiaryRelation)
        formData.append('beneficiaryRelation', values.beneficiaryRelation);
      formData.append('requirement', values.requirement);
      formData.append('urgency', values.urgency);
      if (values.toUserId) formData.append('toUserId', values.toUserId);

      if (values.fulfillers) {
        values.fulfillers.forEach((f) => formData.append('fulfillers', f));
      }
      if (imageUrls[0]) formData.append('imageLink', imageUrls[0]);
      formData.append('attachments', JSON.stringify(attachments));

      setSubmitPhase('saving');
      const { data } = await axiosInstance.post(
        `/api/communities/${communityId}/posts/lead`,
        formData
      );
      toast.success('Lead Created Successfully!');
      parentSubmit(data.postId);
    } catch (error) {
      toast.error(
        reportCreatePostFailure({
          kind: 'lead',
          error,
          userId: currentUserId,
          communityId,
          request: {
            title: values.title,
            beneficiary: values.beneficiary,
            hasBeneficiaryName: Boolean(values.beneficiaryName),
            hasBeneficiaryContact: Boolean(values.beneficiaryContact),
            hasBeneficiaryEmail: Boolean(values.beneficiaryEmail),
            beneficiaryRelation: values.beneficiaryRelation ?? null,
            requirementLength: values.requirement.length,
            urgency: values.urgency,
            fulfillType: values.fulfillType,
            toUserId: values.toUserId ?? null,
            fulfillers: values.fulfillers ?? [],
            imageCount: images.length,
            documentCount: docs.length,
          },
          fallbackMessage: 'Failed to create lead. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  const removeFile = (
    index: number,
    setFileState: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    setFileState((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title={submitPhase === 'uploading' ? 'Uploading files...' : 'Publishing lead...'}
        description={
          submitPhase === 'uploading'
            ? 'Sending images and documents through secure media upload. Please keep this screen open.'
            : 'Saving your lead details.'
        }
      />
      <Card className="mx-auto min-w-0 w-full max-w-5xl animate-in slide-in-from-bottom-5 border-0 bg-background/50 shadow-none backdrop-blur-md duration-300 md:border md:border-border md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            Create Business Lead
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Share a business opportunity with the community.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormInputField
                control={form.control}
                name="title"
                label="Title"
                placeholder="e.g., Need 50 Office Chairs"
                required
              />

              <FormRadioGroupField
                control={form.control}
                name="beneficiary"
                label="Who needs this product/service?"
                required
                layout="row"
                options={BENEFICIARY_OPTIONS}
              />

              {beneficiaryType === 'other' && (
                <div className="grid grid-cols-1 gap-4 rounded-r-md border-l-2 border-primary/20 bg-muted/10 py-2 pl-4 md:grid-cols-2">
                  <FormPhoneField
                    control={form.control}
                    name="beneficiaryContact"
                    label="Contact Number"
                    placeholder="+91 123..."
                    defaultCountry="IN"
                    required
                  />
                  <FormInputField
                    control={form.control}
                    name="beneficiaryName"
                    label="Name"
                    placeholder="John Doe"
                    required
                  />
                  <FormInputField
                    control={form.control}
                    name="beneficiaryEmail"
                    label="Email ID"
                    placeholder="john@example.com"
                  />
                  <FormInputField
                    control={form.control}
                    name="beneficiaryRelation"
                    label="Relation"
                    placeholder="Client / Friend / Family"
                    required
                  />
                </div>
              )}

              <FormTextareaField
                control={form.control}
                name="requirement"
                label={
                  beneficiaryType === 'self'
                    ? 'What product/service do you want to avail?'
                    : `What product/service does ${form.getValues('beneficiaryName') || 'they'} want to avail?`
                }
                placeholder="Describe the requirement... Use @ for a member or business, # for a category"
                maxLength={1000}
                required
                mentions
              />

              <FormRadioGroupField
                control={form.control}
                name="fulfillType"
                label="Who should fulfill this lead?"
                required
                layout="row"
                options={FULFILL_TYPE_OPTIONS}
              />

              {fulfillType === 'open' ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <label className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mt-1">
                      Which business category can fulfill this?{' '}
                      <span className="text-primary">*</span>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 gap-1 px-2 text-xs text-primary shadow-sm hover:bg-primary/10"
                      onClick={handlePredictCategory}
                      disabled={isPredicting || !form.watch('title') || !form.watch('requirement')}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {isPredicting ? 'Predicting...' : 'Auto-Predict'}
                    </Button>
                  </div>
                  <FormMultiSelectField
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    control={form.control as any}
                    name="fulfillers"
                    placeholder="Select categories..."
                    options={categoryOptions.length > 0 ? categoryOptions : FULFILLER_OPTIONS}
                    onSearch={handleCategorySearch}
                  />
                </div>
              ) : (
                <FormComboboxField
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={form.control as any}
                  name="toUserId"
                  label="Select Member"
                  placeholder="Click to choose a member"
                  searchPlaceholder="Search member by name, business, category, sub-category, keywords..."
                  required
                  options={[
                    ...frequentMembers
                      .filter((m) => m.id !== currentUserId)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((m) => {
                        const details = [
                          m.businessCategory ? `Cat: ${m.businessCategory}` : null,
                          m.subCategory ? `Sub: ${m.subCategory}` : null,
                          m.keywords ? `Keywords: ${m.keywords}` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ');

                        return {
                          value: m.id,
                          label: m.businessName ? `${m.name} (${m.businessName})` : m.name,
                          description: details || undefined,
                          searchValue: `${m.name} ${m.businessName || ''} ${m.businessCategory || ''} ${m.subCategory || ''} ${m.keywords || ''}`,
                          group: 'Frequent',
                        };
                      }),
                    ...members
                      .filter((m) => m.id !== currentUserId)
                      .map((m) => {
                        const details = [
                          m.businessCategory ? `Cat: ${m.businessCategory}` : null,
                          m.subCategory ? `Sub: ${m.subCategory}` : null,
                          m.keywords ? `Keywords: ${m.keywords}` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ');

                        return {
                          value: m.id,
                          label: m.businessName ? `${m.name} (${m.businessName})` : m.name,
                          description: details || undefined,
                          searchValue: `${m.name} ${m.businessName || ''} ${m.businessCategory || ''} ${m.subCategory || ''} ${m.keywords || ''}`,
                          group: 'Members',
                        };
                      }),
                  ]}
                />
              )}

              <div className="space-y-2">
                <SectionLabel>Upload Image (Optional)</SectionLabel>
                <div className="flex items-center gap-4">
                  <FileUploadButton
                    id="image-upload"
                    accept="image/*"
                    onFiles={(files) => setImages((prev) => [...prev, ...files])}
                    label="Select Images"
                    icon={Upload}
                  />
                </div>
                <FileAttachmentList files={images} onRemove={(i) => removeFile(i, setImages)} />
              </div>

              <FormRadioGroupField
                control={form.control}
                name="urgency"
                label="When is the product/service required?"
                required
                layout="scroll-x"
                options={URGENCY_OPTIONS}
              />

              <div className="space-y-2">
                <SectionLabel>Additional Documents (Optional)</SectionLabel>
                <div className="flex items-center gap-4">
                  <FileUploadButton
                    id="doc-upload"
                    accept=".pdf,.doc,.docx,.txt,image/*"
                    onFiles={(files) => setDocs((prev) => [...prev, ...files])}
                    label="Attach Docs"
                    icon={Paperclip}
                  />
                </div>
                <FileAttachmentList files={docs} onRemove={(i) => removeFile(i, setDocs)} />
              </div>

              <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
                >
                  {isSubmitting ? 'Publishing...' : 'Publish Lead'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
}
